import { Link } from 'react-router-dom'
import { m } from 'motion/react'
import { Button } from '@/components/ui'
import { FugueContactForm } from '@/components/FugueContactForm'
import { useSEO, createServiceSchema } from '@/hooks/useSEO'
import { useHeroReveal } from '@gjl/motion/css'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'
import { ArrowRight } from 'lucide-react'

const services = [
  {
    title: 'Website Design & Development',
    description:
      'Custom websites that are fast, beautiful, and effective. Designed for classical musicians and arts groups.',
    href: '/services/websites',
  },
  {
    title: 'Graphic Design',
    description:
      'Brand identity, posters, programs, and season campaigns. One-time projects or a monthly design retainer.',
    href: '/services/design',
  },
  {
    title: 'Photography',
    description:
      'Concert coverage, headshots, and season photography days, delivered as edited galleries you own outright.',
    href: '/services/photography',
  },
  {
    title: 'Digital Strategy & Consulting',
    description:
      'Strategy intensives and fractional digital direction: audit, roadmap, and ongoing oversight without full execution.',
    href: '/pricing#consulting',
  },
  {
    title: 'Packages & Pricing',
    description: 'Clear, affordable options with payment plans for soloists, ensembles, and organizations.',
    href: '/pricing',
  },
  {
    title: 'Industries We Serve',
    description: 'See how we support solo artists, educators, nonprofits, festivals, and more.',
    href: '/who-we-help',
  },
]

const reasonsToWorkWithUs = [
  {
    title: 'Built for Classical Music',
    description: 'We understand your audience, your needs, and the artistic integrity behind your work.',
  },
  {
    title: 'Elegant, User-Friendly Design',
    description: 'We balance beauty and usability so your site feels like an extension of your artistry.',
  },
  {
    title: 'Systems That Save You Time',
    description: 'Our tools simplify your admin: from scheduling lessons to collecting donations.',
  },
  {
    title: 'One Point of Contact',
    description: 'Work directly with a developer and strategist who knows classical music.',
  },
]

export default function ServicesPage() {
  const breadcrumbs = generateBreadcrumbs('/services', breadcrumbLabels)

  useSEO({
    title: 'Services',
    description:
      'Website design, digital strategy, and automation services for classical music organizations. We build elegant websites and digital tools to help you grow your audience.',
    url: '/services',
    canonical: '/services',
    breadcrumbs,
    schema: createServiceSchema({
      name: 'Digital Services for Classical Music Organizations',
      description:
        'Website design, digital strategy, and automation services for classical music organizations including festivals, ensembles, and choirs.',
      url: '/services',
    }),
  })

  // One entrance, on the hero only; the per-section staggered reveals were
  // the stock kit and are gone.
  const heroRef = useHeroReveal<HTMLDivElement>(0.2)

  return (
    <div className="m-0 mx-auto w-full space-y-28 md:space-y-36">
      {/* Hero */}
      <section className="relative min-h-[90vh] w-full overflow-hidden md:h-[75vh]">
        {/* Same T0 hero treatment as HomePage — scrim for contrast, vignette to
            seat the headline, grain to kill banding across the large flat black.
            `ken-burns` is a slow 1 -> 1.05 push; on a STILL this is the effect's
            namesake use and the thing that stops a static plate reading as one.
            The content block below is `z-10`, so grain (z-1) sits UNDER the copy
            here, where on HomePage the content is `relative`/auto and it sits
            over. Both are fine at 5%; noted so the difference isn't mistaken
            for a bug. */}
        <img
          src="/cello.jpg"
          alt="Cellist performing"
          className="ken-burns absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/80" />
        <div className="fx-vignette" />
        <div className="fx-grain" />
        <div
          ref={heroRef}
          className="relative z-10 mx-auto flex min-h-screen max-w-2xl flex-col items-center justify-center gap-y-6 px-8 text-center text-white"
        >
          <h1 className="text-fluid-h1 tracking-tight">Services for the Classical Music World</h1>
          <p className="text-lg leading-relaxed text-white/80">
            We design elegant websites and digital tools to help you grow your audience, simplify operations, and look
            professional online.
          </p>
          <Link to="/contact">
            <m.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            >
              <Button size="lg" className="group cursor-pointer px-8">
                Start Your Project
                <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Button>
            </m.div>
          </Link>
        </div>
      </section>

      {/* Intro */}
      <section className="mx-auto max-w-3xl px-4 text-center text-lg">
        <p className="text-muted-foreground leading-relaxed">
          Fugue Lab partners with soloists, teachers, ensembles, and organizations to bring modern design and strategy
          to the classical music industry.
        </p>
      </section>

      {/* Services. A large typographic list of links, as on the home page,
          in place of six icon cards: the names carry the section, and hover
          changes the name's colour only. */}
      <section aria-labelledby="services-list" className="mx-auto max-w-6xl px-5 md:px-12">
        <h2 id="services-list" className="sr-only">
          Our services
        </h2>
        <ul className="space-y-2">
          {services.map((service) => (
            <li key={service.title}>
              <Link
                to={service.href}
                className="group focus-visible:outline-primary grid gap-3 py-6 focus-visible:outline-2 focus-visible:outline-offset-4 md:grid-cols-12 md:items-baseline md:gap-8 md:py-8"
              >
                <h3 className="group-hover:text-primary text-[clamp(1.75rem,3.5vw,3rem)] leading-[1.1] font-normal transition-colors duration-200 md:col-span-7">
                  {service.title}
                </h3>
                <span className="text-muted-foreground max-w-[48ch] leading-relaxed md:col-span-5">
                  {service.description}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </section>

      {/* Why Work With Us. Asymmetric 5/7, no icons. */}
      <section
        aria-labelledby="why-fugue-lab"
        className="mx-auto grid max-w-6xl gap-10 px-5 md:grid-cols-12 md:gap-8 md:px-12"
      >
        <h2 id="why-fugue-lab" className="text-fluid-h3 md:col-span-5">
          Why Work With Fugue Lab?
        </h2>
        <div className="grid gap-x-10 gap-y-10 sm:grid-cols-2 md:col-span-7">
          {reasonsToWorkWithUs.map((reason) => (
            <div key={reason.title} className="space-y-2">
              <h3 className="text-xl">{reason.title}</h3>
              <p className="text-muted-foreground leading-relaxed">{reason.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section aria-labelledby="ready" className="mx-auto max-w-3xl space-y-6 px-4 pb-24 text-center">
        <h2 id="ready" className="text-fluid-h2">
          Ready to Start?
        </h2>
        <p className="text-muted-foreground text-lg">
          Whether you&apos;re launching a new site or streamlining your admin, we&apos;d love to hear what you&apos;re
          working on.
        </p>
        <FugueContactForm />
      </section>
    </div>
  )
}
